// DESCRIPTION: Shared utility functions for the calendar components — time formatting and timezone display.

const config = window.nemtlyCalendar || {};

/**
 * Format a time string (HH:MM or HH:MM:SS) according to the configured time format.
 *
 * Supported formats via config.timeFormat:
 *   - '12h'          → 2:30 PM
 *   - '24h'          → 14:30
 *   - 'site_default' → uses the browser's locale default
 *
 * @param {string} time Time string in HH:MM or HH:MM:SS format.
 *
 * @return {string} Formatted time string, or empty string if input is falsy.
 */
export function formatTime( time ) {
	if ( ! time ) {
		return '';
	}

	const parts = time.split( ':' ).map( Number );
	const h = parts[ 0 ] || 0;
	const m = parts[ 1 ] || 0;

	const format = config.timeFormat || 'site_default';

	if ( format === '24h' ) {
		return `${ String( h ).padStart( 2, '0' ) }:${ String( m ).padStart(
			2,
			'0'
		) }`;
	}

	if ( format === '12h' ) {
		const period = h >= 12 ? 'PM' : 'AM';
		const h12 = h % 12 || 12;
		return `${ h12 }:${ String( m ).padStart( 2, '0' ) } ${ period }`;
	}

	// site_default — use browser locale.
	const date = new Date( 2000, 0, 1, h, m );
	return date.toLocaleTimeString( undefined, {
		hour: 'numeric',
		minute: '2-digit',
	} );
}

/**
 * Format an hour number (0–23) as a gutter label according to configured time format.
 *
 * @param {number} hour 0–23
 *
 * @return {string}
 */
export function formatHourLabel( hour ) {
	const format = config.timeFormat || 'site_default';

	if ( format === '24h' ) {
		return `${ String( hour ).padStart( 2, '0' ) }:00`;
	}

	if ( format === '12h' ) {
		if ( hour === 0 ) {
			return '12 AM';
		}
		if ( hour < 12 ) {
			return `${ hour } AM`;
		}
		if ( hour === 12 ) {
			return '12 PM';
		}
		return `${ hour - 12 } PM`;
	}

	// site_default — use browser locale.
	const date = new Date( 2000, 0, 1, hour, 0 );
	return date.toLocaleTimeString( undefined, {
		hour: 'numeric',
		minute: '2-digit',
	} );
}

/**
 * Compute which ISO date strings (YYYY-MM-DD) an event spans.
 *
 * When the event has an explicit end_date that differs from the start date,
 * the span is computed directly from start_date → end_date (inclusive).
 * Otherwise falls back to start_time + duration calculation.
 *
 * @param {Object} event     Calendar event object (needs date_utc/date, time, duration, and optionally end_date).
 * @param {string} gridStart ISO date string of the first visible cell.
 * @param {string} gridEnd   ISO date string of the last visible cell.
 *
 * @return {string[]} Array of ISO date strings the event occupies within the grid window.
 */
export function getEventSpanDates( event, gridStart, gridEnd ) {
	// Use local date when end_date is present (both are in local timezone).
	// Fall back to date_utc for slot-based events that don't have end_date.
	const hasEndDate =
		event.end_date && event.end_date !== ( event.date || '' );
	const startDate = hasEndDate
		? event.date || event.date_utc || ''
		: event.date_utc || event.date || '';
	if ( ! startDate ) {
		return [];
	}

	// If an explicit end_date is provided and differs from start, use date range directly.
	if ( hasEndDate ) {
		const dates = [];
		const current = new Date( startDate + 'T00:00:00' );
		const end = new Date( event.end_date + 'T00:00:00' );

		while ( current <= end ) {
			const iso = current.toISOString().slice( 0, 10 );
			if ( iso >= gridStart && iso <= gridEnd ) {
				dates.push( iso );
			}
			current.setDate( current.getDate() + 1 );
		}

		return dates;
	}

	const durationMin = event.duration || 0;

	// If no time or duration ≤ 0, it's a single-day event.
	if ( ! event.time || durationMin <= 0 ) {
		return startDate >= gridStart && startDate <= gridEnd
			? [ startDate ]
			: [];
	}

	// Parse start datetime.
	const [ h, m ] = event.time.split( ':' ).map( Number );
	const start = new Date( startDate + 'T00:00:00' );
	start.setHours( h || 0, m || 0, 0, 0 );

	// Compute end datetime.
	const end = new Date( start.getTime() + durationMin * 60 * 1000 );

	// Collect each date the event touches.
	const dates = [];
	const current = new Date( start );
	current.setHours( 0, 0, 0, 0 );

	while ( current < end ) {
		const iso = current.toISOString().slice( 0, 10 );
		if ( iso >= gridStart && iso <= gridEnd ) {
			dates.push( iso );
		}
		current.setDate( current.getDate() + 1 );
	}

	return dates;
}

/**
 * Check whether an event spans more than one calendar day.
 *
 * Uses explicit end_date when available (date-based events), otherwise
 * falls back to start_time + duration calculation (slot-based events).
 *
 * @param {Object} event Calendar event object.
 *
 * @return {boolean}
 */
export function isMultiDayEvent( event ) {
	// Use local date for comparison since end_date is in local timezone.
	const startDate = event.date || event.date_utc || '';

	// If end_date is present and differs from start date, it's multi-day.
	if ( event.end_date && event.end_date !== startDate ) {
		return true;
	}

	// Fall back to duration-based detection.
	if ( ! event.time || ! event.duration || event.duration <= 0 ) {
		return false;
	}

	const [ h, m ] = event.time.split( ':' ).map( Number );
	const startMin = ( h || 0 ) * 60 + ( m || 0 );

	// If start time + duration exceeds 24 hours from midnight, it spans multiple days.
	return startMin + event.duration > 1440;
}

/**
 * Get the timezone abbreviation from the site timezone string.
 *
 * @return {string} e.g. "EST", "UTC+5:30", or empty string if unavailable.
 */
export function getTimezoneAbbr() {
	const tz = config.siteTimezone;
	if ( ! tz ) {
		return '';
	}

	try {
		// Use Intl to derive the short abbreviation for the IANA timezone.
		const formatter = new Intl.DateTimeFormat( 'en-US', {
			timeZone: tz,
			timeZoneName: 'short',
		} );
		const parts = formatter.formatToParts( new Date() );
		const tzPart = parts.find( ( p ) => p.type === 'timeZoneName' );
		return tzPart ? tzPart.value : tz;
	} catch {
		// If the timezone string isn't a valid IANA identifier (e.g. "UTC+5"),
		// just return it as-is.
		return tz;
	}
}

/**
 * Get the browser's local timezone abbreviation.
 *
 * @return {string} e.g. "PST", "CET"
 */
export function getLocalTimezoneAbbr() {
	try {
		const formatter = new Intl.DateTimeFormat( 'en-US', {
			timeZoneName: 'short',
		} );
		const parts = formatter.formatToParts( new Date() );
		const tzPart = parts.find( ( p ) => p.type === 'timeZoneName' );
		return tzPart ? tzPart.value : '';
	} catch {
		return '';
	}
}

/**
 * Convert an event's UTC date/time to the browser's local time.
 *
 * Returns an object with the local date string (YYYY-MM-DD) and time string (HH:MM)
 * so callers can use them in place of the event's original date/time.
 *
 * @param {Object} event Calendar event object (needs date_utc, time_utc).
 *
 * @return {{ date: string, time: string }|null} Local date and time, or null if UTC data is missing.
 */
export function convertEventToLocal( event ) {
	if ( ! event.date_utc || ! event.time_utc ) {
		return null;
	}

	const utcStr = `${ event.date_utc }T${ event.time_utc }Z`;
	const localDt = new Date( utcStr );

	if ( isNaN( localDt.getTime() ) ) {
		return null;
	}

	const date = [
		localDt.getFullYear(),
		String( localDt.getMonth() + 1 ).padStart( 2, '0' ),
		String( localDt.getDate() ).padStart( 2, '0' ),
	].join( '-' );

	const time = [
		String( localDt.getHours() ).padStart( 2, '0' ),
		String( localDt.getMinutes() ).padStart( 2, '0' ),
	].join( ':' );

	return { date, time };
}

/**
 * Get the display time for an event, respecting the local timezone toggle.
 *
 * When useLocal is true, converts the event's UTC time to the browser's local time.
 * Otherwise returns the event's original time.
 *
 * @param {Object}  event    Calendar event object.
 * @param {boolean} useLocal Whether to convert to local timezone.
 *
 * @return {string} Time string in HH:MM format.
 */
export function getDisplayTime( event, useLocal ) {
	if ( ! useLocal ) {
		return event.time || '';
	}

	const local = convertEventToLocal( event );
	return local ? local.time : event.time || '';
}

/**
 * Compute column layout for overlapping timed events in a single day.
 *
 * Returns an array of objects with the original event plus layout metadata:
 *   { event, column, totalColumns }
 *
 * Events that share overlapping time ranges are placed in adjacent columns.
 * The algorithm is greedy: sort by start time, then assign each event to the
 * first column where it doesn't overlap with any already-placed event.
 *
 * @param {object[]} events Array of event objects (must have `time` and `duration`).
 *
 * @return {object[]} Array of { event, column, totalColumns }.
 */
export function computeOverlapColumns( events ) {
	if ( ! events.length ) {
		return [];
	}

	// Parse start/end minutes for each event.
	const parsed = events.map( ( event ) => {
		const [ h, m ] = ( event.time || '0:0' ).split( ':' ).map( Number );
		const startMin = ( h || 0 ) * 60 + ( m || 0 );
		const endMin = startMin + ( event.duration || 60 );
		return { event, startMin, endMin };
	} );

	// Sort by start time, then by longer duration first (to fill columns left-to-right).
	parsed.sort(
		( a, b ) =>
			a.startMin - b.startMin ||
			b.endMin - b.startMin - ( a.endMin - a.startMin )
	);

	// columns[i] = endMin of the latest event in column i.
	const columns = [];
	const placed = [];

	parsed.forEach( ( item ) => {
		// Find the first column where this event fits (no overlap).
		let col = -1;
		for ( let c = 0; c < columns.length; c++ ) {
			if ( item.startMin >= columns[ c ] ) {
				col = c;
				break;
			}
		}

		if ( col === -1 ) {
			// No existing column is free — open a new one.
			col = columns.length;
			columns.push( 0 );
		}

		columns[ col ] = item.endMin;
		placed.push( { ...item, column: col } );
	} );

	// Now determine how many columns each group of overlapping events uses.
	// Build overlap groups: events that transitively share time ranges.
	const groups = [];
	const visited = new Set();

	placed.forEach( ( item, idx ) => {
		if ( visited.has( idx ) ) {
			return;
		}

		const group = [ idx ];
		visited.add( idx );

		// Find all events that overlap with any event already in this group.
		let changed = true;
		while ( changed ) {
			changed = false;
			for ( let j = 0; j < placed.length; j++ ) {
				if ( visited.has( j ) ) {
					continue;
				}

				const overlapsWithGroup = group.some( ( gi ) => {
					const a = placed[ gi ];
					const b = placed[ j ];
					return a.startMin < b.endMin && b.startMin < a.endMin;
				} );

				if ( overlapsWithGroup ) {
					group.push( j );
					visited.add( j );
					changed = true;
				}
			}
		}

		groups.push( group );
	} );

	// For each group, determine the max column used + 1 = totalColumns.
	const result = new Array( placed.length );

	groups.forEach( ( group ) => {
		const maxCol = Math.max( ...group.map( ( i ) => placed[ i ].column ) );
		const totalColumns = maxCol + 1;

		group.forEach( ( i ) => {
			result[ i ] = {
				event: placed[ i ].event,
				column: placed[ i ].column,
				totalColumns,
			};
		} );
	} );

	return result.filter( Boolean );
}

/**
 * Get the display date for an event, respecting the local timezone toggle.
 *
 * @param {Object}  event    Calendar event object.
 * @param {boolean} useLocal Whether to convert to local timezone.
 *
 * @return {string} Date string in YYYY-MM-DD format.
 */
export function getDisplayDate( event, useLocal ) {
	if ( ! useLocal ) {
		return event.date_utc || event.date || '';
	}

	const local = convertEventToLocal( event );
	return local ? local.date : event.date_utc || event.date || '';
}
